// src/pages/ParentDashboard.jsx
import React, { useState,useEffect } from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Chip,
  Avatar,
  LinearProgress,
  IconButton,
  Button,
  Badge,
  Fade,
  Container,
  alpha,
} from "@mui/material";
import {
  School,
  CheckCircle,
  Payments,
  EmojiEvents,
  Favorite,
  Share,
  Notifications,
  Chat,
  Download,
  CalendarToday,
  EventAvailable,
  Star,
  ChevronRight,
  TrendingUp,
  LocalLibrary,
  Brush,
  Science,
  MenuBook,
  SportsSoccer,
  MusicNote,
  Today,
} from "@mui/icons-material";
import PaymentsIcon from "@mui/icons-material/Payments";
import apiEndpoints from "../apiconfig";
import { useNavigate } from "react-router-dom";

const ParentDashboard = () => {
  const [favorite, setFavorite] = useState(false);
const navigate = useNavigate();

 const [child, setChild] = useState(null);
 const [recentActivities, setRecentActivities] = useState([]);
 const [upcomingEvents, setUpcomingEvents] = useState([]);
 const [loading, setLoading] = useState(true);

const handleNavigate = (path) => {
  window.scrollTo({ top: 0, behavior: "smooth" });
  navigate(path);
};


useEffect(() => {
  const studentGuid = JSON.parse(
    localStorage.getItem("active_student") || "{}"
  ).guid;

  const token = localStorage.getItem("parent_token");
  if (!studentGuid || !token) return;

  fetch(apiEndpoints.dashboard, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ student_guid: studentGuid }),
  })
    .then((res) => res.json())
    .then((res) => {
      if (res.status === "success") {
        const d = res.data;

       setChild({
         name: d.student.name,
         nickname: d.student.nickname,
         class: d.student.class,
         teacher: d.student.teacher,
         attendance: d.attendance,
         fees: d.fees,
         avatar: d.student.avatar,

         progress: {
           overall: d.progress.overall,
           attendance: d.progress.attendance,
           completion: d.progress.completion,
           avgRating: d.progress.avg_rating,
           avgRatingPercent: d.progress.avg_rating_percent,
         },
       });


        setRecentActivities(d.activities || []);
        setUpcomingEvents(d.events || []);
      }
    })
    .finally(() => setLoading(false));
}, []);


const getActivityMeta = (index) => {
  const icons = [<Science />, <Brush />, <MenuBook />, <MusicNote />, <Star />];

  const colors = ["#7B61FF", "#4CAF50", "#FFB300", "#1976D2", "#FF6B6B"];

  return {
    icon: icons[index % icons.length],
    color: colors[index % colors.length],
  };
};


if (loading || !child) {
  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Typography>Loading dashboard...</Typography>
    </Box>
  );
}

const stats = [
  {
    label: "Class",
    value: child.class,
    icon: <School />,
    color: "#7B61FF",
    info: "Teacher: " + child.teacher,
  },
  {
    label: "Attendance",
    value: `${child.attendance}%`,
    icon: <CheckCircle />,
    color: "#4CAF50",
    info: `Attendance`,
  },
  {
    label: "Fee Status",
    value: child.fees,
    icon: <Payments />,
    color: "#1976D2",
    info: "Fee details",
  },
  {
    label: "Progress Score",
    value: `${child.progress.overall}%`,
    icon: <EmojiEvents />,
    color: "#FFB300",
    info: "Overall performance",
  },
];

  return (
    <Box
      sx={{
        minHeight: "100vh",
        position: "relative",
        overflow: "hidden",
        // Main background with image and overlay
        backgroundImage: `
          linear-gradient(rgba(255, 255, 255, 0.92), rgba(255, 255, 255, 0.92)),
          url('https://images.unsplash.com/photo-1503676260728-1c00da094a0b?auto=format&fit=crop&w=1920')
        `,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundAttachment: "fixed",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            radial-gradient(circle at 20% 80%, rgba(120, 119, 198, 0.1) 0%, transparent 50%),
            radial-gradient(circle at 80% 20%, rgba(255, 119, 198, 0.1) 0%, transparent 50%)
          `,
          pointerEvents: "none",
        },
      }}
    >
      {/* Minimal floating elements */}
      <Box
        sx={{
          position: "absolute",
          top: 100,
          right: 100,
          width: 200,
          height: 200,
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(120, 119, 198, 0.05) 0%, transparent 70%)",
          filter: "blur(40px)",
        }}
      />
      <Box
        sx={{
          position: "absolute",
          bottom: 100,
          left: 100,
          width: 150,
          height: 150,
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(255, 119, 198, 0.05) 0%, transparent 70%)",
          filter: "blur(30px)",
        }}
      />

      <Container
        maxWidth="xl"
        sx={{
          position: "relative",
          zIndex: 1,
          px: { xs: 1.5, sm: 2, md: 4 },
        }}
      >
        {/* HEADER */}
        <Fade in timeout={800}>
          <Box sx={{ py: 4, px: { xs: 2, md: 4 } }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                flexWrap: "wrap",
                gap: 2,
              }}
            >
              <Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    color: "#333",
                    mb: 1,
                    fontSize: { xs: "1.75rem", md: "2.125rem" },
                  }}
                >
                  Welcome, {child.name}'s Parents!
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ color: "text.secondary", fontWeight: 400 }}
                >
                  Track your child's amazing journey and celebrate their growth!
                </Typography>
              </Box>
              {/* <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                <IconButton
                  onClick={() => setFavorite(!favorite)}
                  sx={{
                    bgcolor: "rgba(255, 255, 255, 0.8)",
                    backdropFilter: "blur(10px)",
                    border: "1px solid rgba(0, 0, 0, 0.08)",
                    "&:hover": {
                      bgcolor: "rgba(255, 255, 255, 0.9)",
                    },
                  }}
                >
                  <Favorite sx={{ color: favorite ? "#FF6B6B" : "text.secondary" }} />
                </IconButton>
                <IconButton sx={{ 
                  bgcolor: "rgba(255, 255, 255, 0.8)",
                  backdropFilter: "blur(10px)",
                  border: "1px solid rgba(0, 0, 0, 0.08)",
                  "&:hover": {
                    bgcolor: "rgba(255, 255, 255, 0.9)",
                  },
                }}>
                  <Share />
                </IconButton>
                <Badge badgeContent={3} color="error">
                  <IconButton sx={{ 
                    bgcolor: "rgba(255, 255, 255, 0.8)",
                    backdropFilter: "blur(10px)",
                    border: "1px solid rgba(0, 0, 0, 0.08)",
                    "&:hover": {
                      bgcolor: "rgba(255, 255, 255, 0.9)",
                    },
                  }}>
                    <Notifications />
                  </IconButton>
                </Badge>
              </Box> */}
            </Box>
          </Box>
        </Fade>

        {/* HERO PROFILE */}
        <Fade in timeout={1000}>
          <Paper
            elevation={0}
            sx={{
              mb: 4,
              mx: { xs: 1, sm: 2, md: 4 },
              p: { xs: 3, md: 4 },
              borderRadius: 3,
              background: "rgba(255,255,255,0.85)",
              backdropFilter: "blur(16px)",
              boxShadow: "0 12px 32px rgba(0,0,0,0.08)",
            }}
          >
            <Grid container spacing={4} alignItems="center">
              {/* Avatar */}
              <Grid item xs={12} md={3} sx={{ textAlign: "center" }}>
                <Avatar
                  src={
                    child.avatar?.startsWith("data:image")
                      ? child.avatar
                      : undefined
                  }
                  alt={child.name}
                  sx={{
                    width: { xs: 96, sm: 120, md: 140 },
                    height: { xs: 96, sm: 120, md: 140 },
                    mx: "auto",
                    bgcolor: "#e0e0e0",
                    fontSize: 32,
                    fontWeight: 600,
                    "& img": { objectFit: "cover" },
                  }}
                >
                  {!child.avatar && child.name?.[0]}
                </Avatar>
              </Grid>

              {/* Info */}
              <Grid item xs={12} md={9}>
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                  {child.name}
                </Typography>

                <Typography
                  variant="body2"
                  sx={{ color: "text.secondary", mb: 1 }}
                >
                  Montessori Learner • {child.class}
                </Typography>

                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  <strong>Teacher:</strong> {child.teacher || "Not Assigned"}
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </Fade>

        {/* KEY STATS */}
        <Fade in timeout={1200}>
          <Grid
            container
            spacing={3}
            sx={{ mb: 4, px: { xs: 2, md: 4 } }}
            alignItems="stretch"
          >
            {stats.map((stat, index) => (
              <Grid item xs={12} sm={6} lg={3} key={index}>
                <Paper
                  elevation={0}
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    p: 3,
                    borderRadius: "16px",
                    background: "rgba(255, 255, 255, 0.6)",
                    backdropFilter: "blur(10px)",
                    border: "1px solid rgba(255, 255, 255, 0.3)",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      background: "rgba(255, 255, 255, 0.8)",
                      transform: "translateY(-4px)",
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      mb: 2,
                    }}
                  >
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: "12px",
                        bgcolor: alpha(stat.color, 0.1),
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: stat.color,
                      }}
                    >
                      {stat.icon}
                    </Box>
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{ color: "#666", fontWeight: 500 }}
                      >
                        {stat.label}
                      </Typography>
                      <Typography
                        variant="h5"
                        sx={{ fontWeight: 700, color: "#1a1a1a" }}
                      >
                        {stat.value}
                      </Typography>
                    </Box>
                  </Box>
                  <Typography
                    variant="caption"
                    sx={{ color: "#888", display: "block" }}
                  >
                    {stat.info}
                  </Typography>
                  {(stat.label === "Progress Score" ||
                    stat.label === "Attendance") && (
                    <LinearProgress
                      variant="determinate"
                      value={
                        stat.label === "Progress Score"
                          ? child.progress.overall
                          : child.attendance
                      }
                      sx={{
                        mt: 2,
                        height: 6,
                        borderRadius: 3,
                        bgcolor: alpha(stat.color, 0.1),
                        "& .MuiLinearProgress-bar": {
                          borderRadius: 3,
                          bgcolor: stat.color,
                        },
                      }}
                    />
                  )}
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Fade>

        {/* RECENT ACTIVITIES & UPCOMING EVENTS */}
        <Fade in timeout={1400}>
          <Grid
            container
            spacing={3}
            sx={{ mb: 4, px: { xs: 2, md: 4 } }}
            alignItems="stretch"
          >
            {/* RECENT ACTIVITIES */}
            <Grid item xs={12} lg={8}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: "20px",
                  background: "rgba(255, 255, 255, 0.6)",
                  backdropFilter: "blur(10px)",
                  border: "1px solid rgba(255, 255, 255, 0.3)",
                  height: "100%",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 3,
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      color: "#1a1a1a",
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Today /> Recent Achievements & Activities
                  </Typography>
                  <Button
                    endIcon={<ChevronRight />}
                    size="small"
                    sx={{ color: "#666", textTransform: "none" }}
                    onClick={() => handleNavigate("/parent/activities")}
                  >
                    View All
                  </Button>
                </Box>
                <Box sx={{ maxHeight: 400, overflow: "auto", pr: 1 }}>
                  {recentActivities.map((activity, index) => {
                    const meta = getActivityMeta(index);

                    return (
                      <Box
                        key={index}
                        sx={{
                          position: "relative",
                          p: 2.5,
                          mb: 2,
                          borderRadius: "14px",
                          bgcolor: "rgba(255,255,255,0.6)",
                          border: "1px solid rgba(0,0,0,0.04)",
                          overflow: "hidden",
                        }}
                      >
                        {/* LEFT BLUE LINE */}
                        <Box
                          sx={{
                            position: "absolute",
                            left: 0,
                            top: 12,
                            bottom: 12,
                            width: 4,
                            borderRadius: "0 4px 4px 0",
                            bgcolor: "#1976D2", // blue line
                          }}
                        />

                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "flex-start",
                            gap: 2,
                            pl: 2, // ⬅ space so content doesn't touch the line
                          }}
                        >
                          {/* LEFT CONTENT */}
                          <Box>
                            <Typography
                              variant="subtitle2"
                              sx={{ fontWeight: 600 }}
                            >
                              {activity.title}
                            </Typography>

                            <Typography variant="body2" sx={{ color: "#666" }}>
                              {activity.description}
                            </Typography>
                          </Box>

                          {/* RIGHT DATE */}
                          <Chip
                            label={activity.date}
                            size="small"
                            sx={{
                              fontSize: "0.75rem",
                              bgcolor: "rgba(0,0,0,0.05)",
                            }}
                          />
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </Paper>
            </Grid>

            {/* UPCOMING EVENTS */}
            <Grid item xs={12} lg={4}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: "20px",
                  background: "rgba(255, 255, 255, 0.6)",
                  backdropFilter: "blur(10px)",
                  border: "1px solid rgba(255, 255, 255, 0.3)",
                  height: "100%",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    mb: 3,
                    color: "#1a1a1a",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <EventAvailable /> Upcoming Events
                </Typography>
                <Box sx={{ maxHeight: 350, overflow: "auto", pr: 1 }}>
                  {upcomingEvents.map((event, index) => (
                    <Box
                      key={index}
                      sx={{
                        p: 2,
                        mb: 1.5,
                        borderRadius: "12px",
                        bgcolor: "rgba(255, 255, 255, 0.5)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        transition: "all 0.3s ease",
                        "&:hover": {
                          bgcolor: "white",
                          transform: "translateX(4px)",
                        },
                      }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 2 }}
                      >
                        <Typography variant="h5" sx={{ color: "#666" }}>
                          📅
                        </Typography>
                        <Box>
                          <Typography
                            variant="subtitle2"
                            sx={{ fontWeight: 500 }}
                          >
                            {event.name}
                          </Typography>
                          <Typography variant="caption" sx={{ color: "#888" }}>
                            {event.date}
                          </Typography>
                        </Box>
                      </Box>
                      <Chip
                        label="Remind"
                        size="small"
                        sx={{
                          bgcolor: alpha("#1976D2", 0.1),
                          color: "#1976D2",
                          fontWeight: 500,
                          fontSize: "0.75rem",
                        }}
                      />
                    </Box>
                  ))}
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Fade>

        {/* ADDITIONAL STATS */}
        <Fade in timeout={1600}>
          <Grid
            container
            spacing={3}
            sx={{ mb: 4, px: { xs: 2, md: 4 } }}
            alignItems="stretch"
          >
            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                sx={{
                  height: "100%", // ✅
                  display: "flex", // ✅
                  flexDirection: "column", // ✅
                  p: 3,
                  borderRadius: "20px",
                  background: "rgba(255, 255, 255, 0.6)",
                  backdropFilter: "blur(10px)",
                  border: "1px solid rgba(255, 255, 255, 0.3)",
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                  Learning Progress
                </Typography>

                {/* Attendance */}
                <Box sx={{ mb: 2 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      flexWrap: "wrap",
                      gap: 1,
                    }}
                  >
                    <Typography variant="body2">Attendance</Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {child.progress.attendance}%
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={child.progress.attendance}
                    sx={{
                      height: 6,
                      borderRadius: 3,
                      mt: 0.5,
                    }}
                  />
                </Box>

                {/* Task Completion */}
                <Box sx={{ mb: 2 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      flexWrap: "wrap",
                      gap: 1,
                    }}
                  >
                    <Typography variant="body2">Task Completion</Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {child.progress.completion}%
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={child.progress.completion}
                    sx={{
                      height: 6,
                      borderRadius: 3,
                      mt: 0.5,
                    }}
                  />
                </Box>

                {/* Average Marks */}
                <Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      flexWrap: "wrap",
                      gap: 1,
                    }}
                  >
                    <Typography variant="body2">Average Marks</Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {child.progress.avgRatingPercent}%
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={child.progress.avgRatingPercent}
                    sx={{
                      height: 6,
                      borderRadius: 3,
                      mt: 0.5,
                    }}
                  />
                </Box>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                sx={{
                  height: "100%", // ✅
                  display: "flex", // ✅
                  flexDirection: "column", // ✅
                  p: 3,
                  borderRadius: "20px",
                  background: "rgba(255, 255, 255, 0.6)",
                  backdropFilter: "blur(10px)",
                  border: "1px solid rgba(255, 255, 255, 0.3)",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 600, mb: 3, color: "#1a1a1a" }}
                >
                  Quick Actions
                </Typography>
                <Grid container spacing={2}>
                  {[
                    {
                      icon: <LocalLibrary />,
                      label: "View Activities",
                      color: "#7B61FF",
                      path: "/parent/activities",
                    },
                    {
                      icon: <SportsSoccer />,
                      label: "Announcements",
                      color: "#4CAF50",
                      path: "/parent/announcements",
                    },
                    {
                      icon: <MenuBook />,
                      label: "Attendance",
                      color: "#1976D2",
                      path: "/parent/attendance",
                    },
                    {
                      icon: <PaymentsIcon />,
                      label: "Fees & Payments",
                      color: "#FFB300",
                      path: "/parent/fees",
                    },
                  ].map((action, index) => (
                    <Grid item xs={12} sm={6} key={index}>
                      <Paper
                        elevation={0}
                        onClick={() => handleNavigate(action.path)}
                        sx={{
                          p: 2,
                          height: "100%",
                          borderRadius: "14px",
                          bgcolor: "rgba(255,255,255,0.7)",
                          border: "1px solid rgba(0,0,0,0.06)",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 1,
                          cursor: "pointer",
                          transition: "all 0.25s ease",
                          "&:hover": {
                            transform: "translateY(-2px)",
                            borderColor: alpha(action.color, 0.4),
                            bgcolor: "white",
                          },
                        }}
                      >
                        <Box
                          sx={{
                            width: 44,
                            height: 44,
                            borderRadius: "12px",
                            bgcolor: alpha(action.color, 0.15),
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: action.color,
                          }}
                        >
                          {action.icon}
                        </Box>

                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            textAlign: "center",
                            color: "#444",
                          }}
                        >
                          {action.label}
                        </Typography>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        </Fade>

        {/* FOOTER */}
        <Fade in timeout={1800}>
          <Box sx={{ py: 4, textAlign: "center", px: { xs: 2, md: 4 } }}>
            <Typography variant="body2" sx={{ color: "#666", opacity: 0.8 }}>
              © 2025 My School • Made with ❤️ for proud parents
            </Typography>
          </Box>
        </Fade>
      </Container>
    </Box>
  );
};

export default ParentDashboard;